import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useSession } from '@/api/auth';
import { useUserCommitee } from '@/api/commitee/commitee.queries';
import { slugify } from '@/utils/string.utils';

export type Session = {
  user: {
    id: string;
    name: string;
    email: string;
    image?: string | null;
    commiteeSlug?: string;
    commiteeSlugId?: number;
    isAdmin: boolean;
  };
  session: {
    id: string;
    userId: string;
    expiresAt: Date;
  };
};

export type AuthContextType = {
  session: Session | null;
  isSessionPending: boolean;
  isAdmin: boolean;
};

type AuthProviderProps = {
  children: ReactNode;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  isSessionPending: false,
  isAdmin: false,
});

export function AuthProvider({ children }: AuthProviderProps) {
  const { data: session, isPending: isSessionPending } = useSession();
  const { data: commitee, isLoading: isCommiteeLoading } = useUserCommitee();
  const isAuthPending = isSessionPending || (!!session && isCommiteeLoading);

  const enrichedSession = useMemo<Session | null>(() => {
    if (!session) return null;

    if (isCommiteeLoading) {
      return null;
    }

    if (commitee) {
      return {
        ...session,
        user: {
          ...session.user,
          commiteeSlug: slugify(commitee.name),
          commiteeSlugId: commitee.id,
          isAdmin: false,
        },
      };
    }

    return {
      ...session,
      user: {
        ...session.user,
        commiteeSlug: undefined,
        commiteeSlugId: undefined,
        isAdmin: true,
      },
    } as Session;
  }, [session, commitee, isCommiteeLoading]);

  const isAdmin = enrichedSession?.user.isAdmin ?? false;

  return (
    <AuthContext.Provider
      value={{ session: enrichedSession, isSessionPending: isAuthPending, isAdmin }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
